import { PersistConfig, persistReducer } from 'redux-persist';
import { createReducer, getType } from 'typesafe-actions';

import { InstagramActions } from '@lib/state/_actions';
import { produce } from 'immer';
import storage from '@lib/state/storage';

import { InstagramUserInfo, InstagramPost } from '@lib/domain/instagram';
import {
    InstagramState,
    InstagramCompleteOrder,
    InstagramOverviewOrder,
} from './types';

const stateKey = 'instagram';

/* ------------- Initial State ------------- */
const INITIAL_STATE: InstagramState = {
    user: null,
    posts: [],
    overviewOrder: {
        follower: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        like: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        comment: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        view: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
    },
    completeOrder: {
        follower: {
            quantity: 0,
            price: 0,
        },
        like: {
            quantity: 0,
            price: 0,
        },
        comment: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        view: {
            quantity: 0,
            price: 0,
            discount: 0,
        },
        selectedPosts: [],
        selectedVideos: [],
        total: 0,
    },
};

/* ------------- Reducers ------------- */
const setInstagramUser = (
    state: InstagramState,
    { payload: { user } }: ReturnType<typeof InstagramActions.setInstagramUser>,
): InstagramState =>
    produce(state, (draft) => {
        draft.user = user;
        draft.overviewOrder = {
            follower: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            like: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            comment: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            view: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
        };
        draft.completeOrder = {
            follower: {
                quantity: 0,
                price: 0,
            },
            like: {
                quantity: 0,
                price: 0,
            },
            comment: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            view: {
                quantity: 0,
                price: 0,
                discount: 0,
            },
            selectedPosts: [],
            selectedVideos: [],
            total: 0,
        };
    });

const setInstagramPosts = (
    state: InstagramState,
    {
        payload: { posts },
    }: ReturnType<typeof InstagramActions.setInstagramPosts>,
): InstagramState =>
    produce(state, (draft) => {
        draft.posts = posts;
    });

const setInstagramOverviewOrder = (
    state: InstagramState,
    {
        payload: { order },
    }: ReturnType<typeof InstagramActions.setInstagramOverviewOrder>,
): InstagramState =>
    produce(state, (draft) => {
        draft.overviewOrder = order;
    });

const setInstagramCompleteOrder = (
    state: InstagramState,
    {
        payload: { order },
    }: ReturnType<typeof InstagramActions.setInstagramCompleteOrder>,
): InstagramState =>
    produce(state, (draft) => {
        draft.completeOrder = order;
    });

const resetInstagramData = (): InstagramState => INITIAL_STATE;

/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(InstagramActions.setInstagramUser)]: setInstagramUser,
    [getType(InstagramActions.setInstagramPosts)]: setInstagramPosts,
    [getType(InstagramActions.setInstagramOverviewOrder)]:
        setInstagramOverviewOrder,
    [getType(InstagramActions.setInstagramCompleteOrder)]:
        setInstagramCompleteOrder,
    [getType(InstagramActions.resetInstagramData)]: resetInstagramData,
});

const persistConfig: PersistConfig<InstagramState> = {
    key: stateKey,
    storage,
};

const persistedReducer = persistReducer<
    InstagramState,
    InstagramActions.InstagramActions
>(persistConfig, reducer);

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
// eslint-disable-next-line
const getReducerState = (state: any): InstagramState => state[stateKey];

const selectors = {
    getInstagramUser: (state: InstagramState): InstagramUserInfo | null =>
        state.user,
    getInstagramPosts: (state: InstagramState): InstagramPost[] => state.posts,
    getInstagramOverviewOrder: (
        state: InstagramState,
    ): InstagramOverviewOrder => state.overviewOrder,
    getInstagramCompleteOrder: (
        state: InstagramState,
    ): InstagramCompleteOrder => state.completeOrder,
};

/* ------------- Export ------------- */
const reducerObject = {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer: persistedReducer,
};

export default reducerObject;
